import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import type { DateRange } from '@shared/components/FilterPopup';
import { listCertificateTemplates } from '../../api/listCertificateTemplatesApiService';
import type { CertificateTemplateListItem } from '../../types';

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;
const DEFAULT_LIMIT = 10;
const MAX_SEARCH_LENGTH = 200;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const emptyDateRange: DateRange = { startDate: '', endDate: '' };

const isAllowedLimit = (v: number): boolean =>
  (ALLOWED_LIMITS as readonly number[]).includes(v);

const sanitizeDateParam = (value: string | null): string => {
  if (!value) {
    return '';
  }
  const trimmed = value.trim();
  if (!ISO_DATE_REGEX.test(trimmed)) {
    return '';
  }
  return Number.isNaN(new Date(trimmed).getTime()) ? '' : trimmed;
};

const hasDateFilter = (range: DateRange): boolean =>
  Boolean((range.startDate ?? '').trim() || (range.endDate ?? '').trim());

export function useCertificateTemplateList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);
  const lastWrittenRef = useRef<string | null>(null);

  const [items, setItems] = useState<CertificateTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isNoFilterFetch, setIsNoFilterFetch] = useState(false);
  const [limitError, setLimitError] = useState('');

  const [page, setPage] = useState(() => {
    const v = parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isNaN(v) || v < 1 ? 1 : v;
  });
  const [limit, setLimit] = useState(() => {
    const v = parseInt(searchParams.get('limit') ?? String(DEFAULT_LIMIT), 10);
    return isAllowedLimit(v) ? v : DEFAULT_LIMIT;
  });
  const [search, setSearchState] = useState(() => {
    const v = (searchParams.get('search') ?? '').trim();
    return v.slice(0, MAX_SEARCH_LENGTH);
  });
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    startDate: sanitizeDateParam(searchParams.get('startDate')),
    endDate: sanitizeDateParam(searchParams.get('endDate')),
  }));
  const [appliedFilters, setAppliedFilters] = useState<{
    dateRange: DateRange;
  }>(() => ({
    dateRange: {
      startDate: sanitizeDateParam(searchParams.get('startDate')),
      endDate: sanitizeDateParam(searchParams.get('endDate')),
    },
  }));

  const debouncedSearch = useDebounce(search, 500);
  const prevDebouncedRef = useRef(debouncedSearch);

  // State → URL
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      return;
    }
    const params = new URLSearchParams();
    // Preserve tab param so navigating doesn't drop the active tab.
    const tab = searchParams.get('tab');
    if (tab) {
      params.set('tab', tab);
    }
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    if (search.trim()) {
      params.set('search', search.trim());
    }
    const start = (appliedFilters.dateRange.startDate ?? '').trim();
    const end = (appliedFilters.dateRange.endDate ?? '').trim();
    if (start) {
      params.set('startDate', start);
    }
    if (end) {
      params.set('endDate', end);
    }
    const str = params.toString();
    if (str === lastWrittenRef.current) {
      return;
    }
    lastWrittenRef.current = str;
    setSearchParams(params, { replace: true });
  }, [page, limit, search, appliedFilters, setSearchParams, searchParams]);

  // URL → State (browser back/forward)
  useEffect(() => {
    const str = new URLSearchParams(searchParams).toString();
    if (str === lastWrittenRef.current) {
      return;
    }
    isSyncingFromUrl.current = true;
    const urlPage = parseInt(searchParams.get('page') ?? '1', 10);
    const urlLimit = parseInt(
      searchParams.get('limit') ?? String(DEFAULT_LIMIT),
      10
    );
    const urlSearch = (searchParams.get('search') ?? '')
      .trim()
      .slice(0, MAX_SEARCH_LENGTH);
    const urlStart = sanitizeDateParam(searchParams.get('startDate'));
    const urlEnd = sanitizeDateParam(searchParams.get('endDate'));

    setPage(Number.isNaN(urlPage) || urlPage < 1 ? 1 : urlPage);
    setLimit(isAllowedLimit(urlLimit) ? urlLimit : DEFAULT_LIMIT);
    setSearchState(urlSearch);
    const nextRange: DateRange = { startDate: urlStart, endDate: urlEnd };
    setDateRange(nextRange);
    setAppliedFilters({ dateRange: nextRange });

    requestAnimationFrame(() => {
      isSyncingFromUrl.current = false;
    });
  }, [searchParams]);

  // Reset page → 1 when debounced search changes
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      prevDebouncedRef.current = debouncedSearch;
      return;
    }
    if (prevDebouncedRef.current.trim() !== debouncedSearch.trim()) {
      prevDebouncedRef.current = debouncedSearch;
      if (page !== 1) {
        setPage(1);
      }
    }
  }, [debouncedSearch, page]);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const trimmed = debouncedSearch.trim();
      const start = (appliedFilters.dateRange.startDate ?? '').trim();
      const end = (appliedFilters.dateRange.endDate ?? '').trim();
      const result = await listCertificateTemplates({
        page,
        limit,
        ...(trimmed && { search: trimmed }),
        ...(start && { startDate: start }),
        ...(end && { endDate: end }),
      });

      if (
        result.pagination.totalCount > 0 &&
        result.pagination.totalPages > 0 &&
        page > result.pagination.totalPages
      ) {
        setPage(result.pagination.totalPages);
        return;
      }

      setItems(result.data);
      setTotalCount(result.pagination.totalCount);
      setIsNoFilterFetch(!trimmed && !start && !end);
    } catch {
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch, appliedFilters]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSearchChange = useCallback(
    (next: string) => {
      const normalized = next.trimStart();
      if (normalized.length > MAX_SEARCH_LENGTH) {
        return;
      }
      if (!normalized.trim() && search.trim()) {
        setLoading(true);
      }
      setSearchState(normalized);
    },
    [search]
  );

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!isAllowedLimit(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ dateRange });
    setPage(1);
  }, [dateRange]);

  const handleClearFilters = useCallback(() => {
    const hadFilters = hasDateFilter(appliedFilters.dateRange);
    setDateRange(emptyDateRange);
    if (hadFilters) {
      setAppliedFilters({ dateRange: emptyDateRange });
      setPage(1);
    }
  }, [appliedFilters]);

  const isSearchClearing =
    !search.trim() &&
    debouncedSearch.trim() !== '' &&
    !hasDateFilter(appliedFilters.dateRange);

  return {
    items,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch: handleSearchChange,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    refetch: fetchTemplates,
  };
}
